import React, { useCallback, useEffect, useMemo } from 'react';
import ModalContainer from 'components/Modal/ModalContainer/ModalContainer';
import { Picture } from 'components/Global/Picture/Picture';
import TextRender from 'components/Global/TextRender/TextRender';
import { ISavedConfigModalContent } from 'interfaces/modal';
import { useStyles } from './SavedConfigModal.styles';
import { Image } from 'interfaces/image';
import {
  LinkButton,
  LinkButtonType,
} from 'components/Global/LinkButton/LinkButton';
import { pageLoad, trackEvent } from 'helpers/Analytics';
import { useSelector } from 'react-redux';
import { getPageLoadSavedConfig } from 'redux/slices/savedConfigSlice';
import { cleanHTMLFromString } from 'helpers/FormatText';
import { IPageLoadData } from 'interfaces/analytics';

export interface ISavedConfigModal {
  referrerPage: string;
  cancelFunction: () => void;
  continueFunction: () => void;
  data?: ISavedConfigModalContent;
  isOpen: boolean;
  closePageLoadTracking?: IPageLoadData;
}

const SavedConfigModal = ({
  referrerPage,
  cancelFunction,
  continueFunction,
  data,
  isOpen,
  closePageLoadTracking,
}: ISavedConfigModal): JSX.Element => {
  const classes = useStyles();
  const pageLoadObj = useSelector(getPageLoadSavedConfig);
  const { title, content, yesButton, noButton } = data || {};
  const {
    modelId,
    modelSeries,
    modelTrim,
    modelYear,
    accessories,
    buildAndPrice,
  } = pageLoadObj;
  const { exteriorColor } = buildAndPrice;

  const imagePath = `/platform/api/v4/images/Exterior/03?config=M:${modelId}$EC:${exteriorColor}$IC:INT$BI:false$O:${accessories.join(
    ','
  )}:$PL:allangles&width=512&filetype=png&background=transparent&modelSeries=${modelSeries}&modelYear=${modelYear}`;
  const carPicture: Image = {
    alternativeText: 'Your saved car',
    sources: {
      small: imagePath,
    },
  };

  const getModelSeriesPreposition = (model: string) => {
    const isVowel = /[aeiou]/i.test(model.toLowerCase()[0]);
    return isVowel ? `an ${model}` : `a ${model}`;
  };

  const clickTrackingObject = useMemo(() => {
    return {
      modelSeries: modelSeries || '',
      modelYear: modelYear || '',
      label: '',
      category: {
        includePage: true,
        text: 'saved build selection',
      },
    };
  }, [modelSeries, modelYear]);

  const replaceTokens = useCallback(
    (text: string, withPrep: boolean) => {
      return text
        .replace('[modelId]', modelId)
        .replace(
          '[modelSeries]',
          withPrep ? getModelSeriesPreposition(modelSeries) : modelSeries
        )
        .replace('[modelYear]', modelYear)
        .replace('[exteriorCode]', exteriorColor)
        .replace('[package]', modelTrim);
    },
    [modelId, modelSeries, modelYear, exteriorColor, modelTrim]
  );

  const parsedTitle = title ? replaceTokens(title, false) : '';
  const parsedContent = content ? replaceTokens(content, true) : '';

  const handleCloseModal = () => {
    trackEvent({
      ...clickTrackingObject,
      label: 'close',
      type: 'cta',
    });
    cancelFunction();
  };

  useEffect(() => {
    const isTriggeredPageLoad =
      window.Acr?.dataLayer?.Page?.page_name?.includes(
        'shopping tool consistency modal'
      );
    if (isOpen) {
      pageLoad({
        ...pageLoadObj,
        subSection: referrerPage,
        modelTrim: cleanHTMLFromString(modelTrim),
        pageName: `${referrerPage}:shopping tool consistency modal`,
      });
    } else if (isTriggeredPageLoad && closePageLoadTracking) {
      pageLoad(closePageLoadTracking);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <ModalContainer
      closeFunction={handleCloseModal}
      isOpen={isOpen}
      title={parsedTitle}
      className={`${classes.paper} t-basic-modal`}
      noTracking={true}
    >
      <div className={classes.content}>
        <TextRender
          tagName="h2"
          className={`${classes.content_title} acr-title-4`}
          text={parsedTitle}
        />
        <TextRender
          tagName="p"
          className={classes.content_desc}
          text={parsedContent}
        />
        <Picture
          image={carPicture}
          classNames={{
            container: classes.content_img_container,
            img: classes.content_img,
          }}
        />
        <TextRender
          className={`${classes.content_caption} acr-secondary-title-5`}
          text={`${modelYear} Acura ${modelSeries} ${
            modelTrim && modelSeries.toLowerCase() !== modelTrim.toLowerCase()
              ? `with ${modelTrim}`
              : ''
          }`}
        />
        <div className={classes.content_actions}>
          <LinkButton
            classNames={{
              container: `${classes.content_cta}`,
            }}
            variant={LinkButtonType.redOutlined}
            onClick={cancelFunction}
            trackEventData={{
              ...clickTrackingObject,
              label: noButton?.label || '',
            }}
          >
            <TextRender text={noButton?.label || ''} />
          </LinkButton>
          <LinkButton
            classNames={{
              container: `${classes.content_cta}`,
            }}
            variant={LinkButtonType.red}
            onClick={continueFunction}
            trackEventData={{
              ...clickTrackingObject,
              label: yesButton?.label || '',
            }}
          >
            <TextRender text={yesButton?.label || ''} />
          </LinkButton>
        </div>
      </div>
    </ModalContainer>
  );
};

export default SavedConfigModal;
